import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { formatTime, getTimerColor, shouldFlash } from '../lib/utils';
import { getDeviceId, getDeviceName } from '../lib/deviceId';
import { usePractice } from '../contexts/PracticeContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Clock, ChevronDown, User, Stethoscope, HelpCircle, X, Monitor, AlertCircle, Mail, Send, AlertTriangle, ChevronLeft } from 'lucide-react';
import { createPortal } from 'react-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper functions to safely get first/last name
function getFirstName(user) {
  if (!user) return '';
  if (user.first_name) return user.first_name;
  if (user.full_name) {
    const parts = user.full_name.split(' ');
    return parts[0] || '';
  }
  return '';
}

function getLastName(user) {
  if (!user) return '';
  if (user.last_name) return user.last_name;
  if (user.full_name) {
    const parts = user.full_name.split(' ');
    return parts.length > 1 ? parts[parts.length - 1] : '';
  }
  return '';
}

// ============ RAN LONG DIALOG ============
function RanLongDialog({ isOpen, onClose, onConfirm, waitingOperatories }) {
  const [ranLong, setRanLong] = useState(false);
  const [selectedMinutes, setSelectedMinutes] = useState(5);
  
  const minuteOptions = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60];
  
  const handleConfirm = () => {
    onConfirm(ranLong, ranLong ? selectedMinutes : 0);
  };
  
  if (!isOpen) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="w-5 h-5" />
            Patient Waiting in Other Operatory
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          <p className="text-sm text-slate-600 mb-4">
            You have patients waiting past the critical time:
          </p>
          
          <div className="space-y-2 mb-6">
            {waitingOperatories.map((op, index) => (
              <div key={op.operatory_id + '-' + index} className={`p-3 rounded-lg border ${
                op.is_same_operatory 
                  ? 'bg-dp-50 border-dp-200' 
                  : 'bg-amber-50 border-amber-200'
              }`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-800">{op.operatory_name}</span>
                  <span className={`font-mono text-sm font-semibold ${
                    op.is_same_operatory ? 'text-dp-700' : 'text-amber-700'
                  }`}>
                    Total Wait: {op.total_wait_formatted || op.seated_elapsed_formatted}
                  </span>
                </div>
                {op.patient_state === 'next_waiting' && (
                  <div className="text-xs text-dp-600 mt-1">
                    Next patient in this operatory
                  </div>
                )}
                {op.seated_elapsed_formatted && op.patient_state !== 'arrived' && op.patient_state !== 'next_waiting' && (
                  <div className="text-xs text-slate-500 mt-1">
                    In chair: {op.seated_elapsed_formatted}
                  </div>
                )}
                {op.patient_state === 'arrived' && (
                  <div className="text-xs text-slate-500 mt-1">
                    Still in reception
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="border-t border-slate-200 pt-4">
            <p className="text-sm font-medium text-slate-700 mb-3">
              Did this appointment run long?
            </p>
            
            <div className="flex gap-3 mb-4">
              <button
                onClick={() => setRanLong(false)}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                  !ranLong 
                    ? 'bg-slate-700 text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                No
              </button>
              <button
                onClick={() => setRanLong(true)}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                  ranLong 
                    ? 'bg-amber-500 text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Yes, it ran long
              </button>
            </div>
            
            {ranLong && (
              <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm font-medium text-slate-700 mb-3">
                  How many minutes did it run over?
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {minuteOptions.map((mins) => (
                    <button
                      key={mins}
                      onClick={() => setSelectedMinutes(mins)}
                      className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        selectedMinutes === mins
                          ? 'bg-amber-500 text-white'
                          : 'bg-white text-slate-600 hover:bg-amber-100 border border-slate-200'
                      }`}
                    >
                      {mins} min
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onConfirm(false, 0)}>
            Skip
          </Button>
          <Button onClick={handleConfirm} className="bg-dp-600 hover:bg-dp-700">
            Complete Checkout
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ UNDO BUTTON ============
// Requires a half-second hold to activate (prevents accidental clicks)
function UndoButton({ operatoryId, operatoryNumber, onUndo, bgColor, size = 'normal', undoEndpoint = null }) {
  const [isHolding, setIsHolding] = useState(false);
  const [holdProgress, setHoldProgress] = useState(0);
  const holdTimerRef = useRef(null);
  const progressRef = useRef(null);
  
  const HOLD_DURATION = 500; // 500ms hold required
  
  const getSize = () => {
    switch (size) {
      case 'mini': return 'w-8 h-full';
      case 'compact': return 'w-10 h-full';
      default: return 'w-12 h-full';
    }
  };
  
  const getIconSize = () => {
    switch (size) {
      case 'mini': return 'w-3 h-3';
      case 'compact': return 'w-4 h-4';
      default: return 'w-5 h-5';
    }
  };
  
  const handleMouseDown = () => {
    setIsHolding(true);
    setHoldProgress(0);
    
    const startTime = Date.now();
    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / HOLD_DURATION) * 100, 100);
      setHoldProgress(progress);
      
      if (progress >= 100) {
        clearInterval(progressRef.current);
      }
    }, 16);
    
    holdTimerRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        // Use custom endpoint if provided, otherwise default to /undo
        const endpoint = undoEndpoint || `${API}/operatory/${operatoryId}/undo`;
        await axios.post(endpoint, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        onUndo?.();
        toast.success('Action undone');
      } catch (error) {
        console.error('Undo error:', error);
        toast.error('Failed to undo');
      }
      setIsHolding(false);
      setHoldProgress(0);
    }, HOLD_DURATION);
  };
  
  const handleMouseUp = () => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
    }
    if (progressRef.current) {
      clearInterval(progressRef.current);
    }
    setIsHolding(false);
    setHoldProgress(0);
  };
  
  const handleMouseLeave = () => {
    handleMouseUp();
  };
  
  // Make the button slightly lighter (5-10%) than the base color
  const getLighterColor = (color) => {
    // Parse hex color and lighten by mixing with white
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Lighten by 8% (mix with white)
    const factor = 0.08;
    const newR = Math.min(255, Math.round(r + (255 - r) * factor));
    const newG = Math.min(255, Math.round(g + (255 - g) * factor));
    const newB = Math.min(255, Math.round(b + (255 - b) * factor));
    
    return `rgb(${newR}, ${newG}, ${newB})`;
  };
  
  return (
    <button
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleMouseDown}
      onTouchEnd={handleMouseUp}
      className={`${getSize()} rounded-l-lg flex items-center justify-center transition-all relative overflow-hidden`}
      style={{ 
        backgroundColor: getLighterColor(bgColor)
      }}
      title="Hold to undo (0.5s)"
      data-testid={`undo-btn-${operatoryNumber}`}
    >
      {/* Progress indicator */}
      {isHolding && (
        <div 
          className="absolute bottom-0 left-0 right-0 bg-white/50 transition-all"
          style={{ height: `${holdProgress}%` }}
        />
      )}
      <ChevronLeft className={`${getIconSize()} text-white/70 relative z-10`} />
    </button>
  );
}

// ============ PATIENT FLOW BUTTON ============
function PatientFlowButton({ operatory, onUpdate, size = 'normal', buttonColors = {} }) {
  const { practice } = usePractice();
  
  // Get custom colors from props or use defaults
  const arrivedColor = buttonColors.patient_arrived || '#0284c7'; // sky-600
  const seatedColor = buttonColors.patient_seated || '#059669'; // emerald-600
  const drInRoomColor = buttonColors.dr_in_room || '#4f46e5'; // indigo-600
  const [state, setState] = useState('empty'); // empty, arrived, seated, dr_in_room, seated_with_next
  const [arrivedTime, setArrivedTime] = useState(null);
  const [seatedTime, setSeatedTime] = useState(null);
  const [drInRoomTime, setDrInRoomTime] = useState(null);
  const [nextArrivedTime, setNextArrivedTime] = useState(null);
  const [arrivedElapsed, setArrivedElapsed] = useState(0);
  const [seatedElapsed, setSeatedElapsed] = useState(0);
  const [drInRoomElapsed, setDrInRoomElapsed] = useState(0);
  const [nextElapsed, setNextElapsed] = useState(0);
  
  // Ran-long dialog state
  const [showRanLongDialog, setShowRanLongDialog] = useState(false);
  const [waitingOperatories, setWaitingOperatories] = useState([]);

  const warningThreshold = practice?.warning_threshold || 300;
  const criticalThreshold = practice?.critical_threshold || 420;
  const drInRoomEnabled = practice?.dr_in_room_enabled || false;
  
  // Use escalation state from operatory (shared with AlertStack)
  const isEscalated = operatory.arrived_escalated || false;
  const isNextEscalated = operatory.next_arrived_escalated || false;

  // Size-based styling
  const getButtonHeight = () => {
    switch (size) {
      case 'compact': return 'h-10';
      case 'mini': return 'h-8';
      default: return 'h-12';
    }
  };

  const getTextSize = () => {
    switch (size) {
      case 'compact': return 'text-sm';
      case 'mini': return 'text-xs';
      default: return 'text-base';
    }
  };

  const getIconSize = () => {
    switch (size) {
      case 'compact': return 'w-4 h-4';
      case 'mini': return 'w-3 h-3';
      default: return 'w-5 h-5';
    }
  };

  const buttonHeight = getButtonHeight();
  const textSize = getTextSize();
  const iconSize = getIconSize();

  // Load state from operatory — immediately compute elapsed times to prevent animation flash
  useEffect(() => {
    if (operatory.patient_flow_state) {
      setState(operatory.patient_flow_state);
      if (operatory.arrived_at) {
        const t = new Date(operatory.arrived_at);
        setArrivedTime(t);
        setArrivedElapsed(Math.floor((Date.now() - t.getTime()) / 1000));
      }
      if (operatory.seated_at) {
        const t = new Date(operatory.seated_at);
        setSeatedTime(t);
        setSeatedElapsed(Math.floor((Date.now() - t.getTime()) / 1000));
      }
      if (operatory.dr_in_room_at) {
        const t = new Date(operatory.dr_in_room_at);
        setDrInRoomTime(t);
        setDrInRoomElapsed(Math.floor((Date.now() - t.getTime()) / 1000));
      }
      if (operatory.next_arrived_at) {
        const t = new Date(operatory.next_arrived_at);
        setNextArrivedTime(t);
        setNextElapsed(Math.floor((Date.now() - t.getTime()) / 1000));
      }
    } else {
      setState('empty');
      setArrivedTime(null);
      setSeatedTime(null);
      setDrInRoomTime(null);
      setNextArrivedTime(null);
    }
  }, [operatory]);

  // Timer updates
  useEffect(() => {
    const interval = setInterval(() => {
      if (arrivedTime && (state === 'arrived' || state === 'arrived_with_next')) {
        setArrivedElapsed(Math.floor((Date.now() - arrivedTime.getTime()) / 1000));
      }
      if (seatedTime && (state === 'seated' || state === 'seated_with_next' || state === 'dr_in_room' || state === 'dr_in_room_with_next')) {
        setSeatedElapsed(Math.floor((Date.now() - seatedTime.getTime()) / 1000));
      }
      if (drInRoomTime && (state === 'dr_in_room' || state === 'dr_in_room_with_next')) {
        setDrInRoomElapsed(Math.floor((Date.now() - drInRoomTime.getTime()) / 1000));
      }
      if (nextArrivedTime && (state === 'arrived_with_next' || state === 'seated_with_next' || state === 'dr_in_room_with_next')) {
        setNextElapsed(Math.floor((Date.now() - nextArrivedTime.getTime()) / 1000));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [arrivedTime, seatedTime, drInRoomTime, nextArrivedTime, state]);

  const handlePatientArrived = async () => {
    try {
      // Optimistic reset — immediately clear stale state to prevent flash of previous patient's state
      setState('arrived');
      setArrivedTime(new Date());
      setArrivedElapsed(0);
      setSeatedTime(null);
      setSeatedElapsed(0);
      setDrInRoomTime(null);
      setDrInRoomElapsed(0);
      setNextArrivedTime(null);
      setNextElapsed(0);
      
      await axios.post(`${API}/operatory/${operatory.id}/patient-arrived`, {
        device_id: getDeviceId()
      });
      toast.success('Patient arrived');
      onUpdate();
    } catch (error) {
      toast.error('Failed to update status');
      onUpdate(); // Refresh to get correct state on error
    }
  };

  const handlePatientSeated = async () => {
    try {
      await axios.post(`${API}/operatory/${operatory.id}/patient-seated`);
      toast.success('Patient seated');
      onUpdate();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDrInRoom = async () => {
    try {
      await axios.post(`${API}/operatory/${operatory.id}/dr-in-room`);
      toast.success('Doctor in room');
      onUpdate();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleNextPatientArrived = async () => {
    try {
      await axios.post(`${API}/operatory/${operatory.id}/next-patient-arrived`, {
        device_id: getDeviceId()
      });
      toast.success('Next patient arrived');
      onUpdate();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleCheckout = async (ranLong = false, ranLongMinutes = 0) => {
    try {
      await axios.post(`${API}/operatory/${operatory.id}/checkout`, {
        ran_long: ranLong,
        ran_long_minutes: ranLongMinutes
      });
      toast.success('Patient checked out');
      setShowRanLongDialog(false);
      onUpdate();
    } catch (error) {
      toast.error('Failed to checkout');
    }
  };

  const initiateCheckout = async () => {
    // Only check for waiting operatories if there's a provider assigned
    if (!operatory.current_provider_id) {
      handleCheckout(false, 0);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/operatory/${operatory.id}/check-waiting-operatories`);
      if (response.data.has_waiting && response.data.waiting_operatories.length > 0) {
        setWaitingOperatories(response.data.waiting_operatories);
        setShowRanLongDialog(true);
      } else {
        handleCheckout(false, 0);
      }
    } catch (error) {
      // If check fails, just proceed with checkout
      handleCheckout(false, 0);
    }
  };

  const handleRanLongConfirm = (ranLong, minutes) => {
    handleCheckout(ranLong, minutes);
  };

  const getArrivedColor = () => getTimerColor(arrivedElapsed, warningThreshold, criticalThreshold);
  const getNextColor = () => getTimerColor(nextElapsed, warningThreshold, criticalThreshold);
  const isArrivedFlashing = () => shouldFlash(arrivedElapsed, criticalThreshold);
  const isNextFlashing = () => shouldFlash(nextElapsed, criticalThreshold);
  const isArrivedCritical = () => getArrivedColor() === 'critical';
  const isNextCritical = () => getNextColor() === 'critical';

  const getButtonStyle = (timerColor, isFlashing, escalated = false) => {
    let base = 'transition-all font-semibold text-white ';
    if (timerColor === 'critical') {
      if (escalated) {
        // Escalated: intense pulsing with thick border
        return base + 'animate-pulse-escalated border-2 border-red-300 shadow-lg shadow-red-500/50';
      }
      // Critical (not escalated): subtle pulse with thin border, less intense
      return base + 'animate-pulse-subtle border border-red-400';
    }
    if (timerColor === 'warning') {
      // Warning: no animation, just the color (set via inline style)
      return base;
    }
    // Default/neutral: use custom color via inline style
    return base;
  };

  // Get background color based on timer state - always returns a color, never black/white
  const getTimerBgColor = (timerColor, customColor, warningColor = '#ef4444', criticalColor = '#dc2626') => {
    if (timerColor === 'critical') return criticalColor;
    if (timerColor === 'warning') return warningColor;
    return customColor; // Use the custom color from settings
  };

  // Helper to darken a hex color for hover state
  const darkenColor = (hex, percent = 10) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.max(0, (num >> 16) - amt);
    const G = Math.max(0, ((num >> 8) & 0x00FF) - amt);
    const B = Math.max(0, (num & 0x0000FF) - amt);
    return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
  };

  const handleEscalate = async (e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/operatory/${operatory.id}/toggle-escalation`, {
        alert_type: 'arrived'
      });
      onUpdate(); // Refresh to get new state
    } catch (error) {
      console.error('Failed to toggle escalation:', error);
    }
  };

  const handleEscalateNext = async (e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/operatory/${operatory.id}/toggle-escalation`, {
        alert_type: 'next_arrived'
      });
      onUpdate(); // Refresh to get new state
    } catch (error) {
      console.error('Failed to toggle escalation:', error);
    }
  };

  // Empty state - show "Patient Arrived" button
  if (state === 'empty') {
    return (
      <>
        <Button
          onClick={handlePatientArrived}
          data-testid={`patient-arrived-btn-${operatory.number}`}
          className={`w-full ${buttonHeight} rounded-xl text-white font-semibold ${textSize} transition-all duration-150 hover:scale-[1.02] hover:brightness-110`}
          style={{ backgroundColor: arrivedColor }}
        >
          Patient Arrived
        </Button>
        <RanLongDialog 
          isOpen={showRanLongDialog}
          onClose={() => setShowRanLongDialog(false)}
          onConfirm={handleRanLongConfirm}
          waitingOperatories={waitingOperatories}
        />
      </>
    );
  }

  // Arrived state - show timer, click to seat
  if (state === 'arrived') {
    const color = getArrivedColor();
    const flashing = isArrivedFlashing();
    const critical = isArrivedCritical();
    // Always use custom color via inline style - getTimerBgColor handles warning/critical
    const bgColor = getTimerBgColor(color, arrivedColor);
    return (
      <>
        <div className={`flex gap-1 ${buttonHeight} overflow-hidden`}>
          <div className="flex flex-1 min-w-0 overflow-hidden">
            <UndoButton 
              operatoryId={operatory.id}
              operatoryNumber={operatory.number}
              onUndo={onUpdate} 
              bgColor={bgColor} 
              size={size}
            />
            <Button
              onClick={handlePatientSeated}
              data-testid={`patient-seated-btn-${operatory.number}`}
              className={`flex-1 h-full rounded-r-xl rounded-l-none min-w-0 ${getButtonStyle(color, flashing, isEscalated)} ${textSize} transition-all duration-150 hover:brightness-110`}
              style={{ backgroundColor: bgColor }}
            >
              <div className="flex items-center justify-between w-full min-w-0 overflow-hidden">
                <span className="truncate shrink-1">{critical && size !== 'normal' ? 'Arr' : size === 'mini' ? 'Arrived' : 'Pt. Arrived'}</span>
                <div className="flex items-center gap-1 ml-1 shrink-0">
                  {critical && (
                    <button
                      onClick={handleEscalate}
                      data-testid={`escalate-arrived-${operatory.number}`}
                      className={`${size === 'mini' ? 'w-4 h-4' : 'w-5 h-5'} rounded-full flex items-center justify-center transition-all shrink-0 ${
                        isEscalated ? 'ring-2 ring-red-400 scale-110' : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: '#ffffff' }}
                    >
                      <AlertCircle className={`${size === 'mini' ? 'w-3 h-3' : 'w-4 h-4'} ${isEscalated ? 'text-red-600' : 'text-red-500'}`} />
                    </button>
                  )}
                  <span className="font-mono tabular-nums">{formatTime(arrivedElapsed)}</span>
                </div>
              </div>
            </Button>
          </div>
          <Button
            onClick={handleNextPatientArrived}
            data-testid={`next-arrived-btn-${operatory.number}`}
            className={`h-full ${size === 'mini' ? 'w-7' : 'w-9'} shrink-0 rounded-full text-white font-semibold flex items-center justify-center transition-all duration-150 hover:brightness-110`}
            style={{ backgroundColor: arrivedColor }}
            title="Next Patient Arrived"
          >
            <User className={size === 'mini' ? 'w-3 h-3' : 'w-4 h-4'} />
          </Button>
        </div>
        <RanLongDialog 
          isOpen={showRanLongDialog}
          onClose={() => setShowRanLongDialog(false)}
          onConfirm={handleRanLongConfirm}
          waitingOperatories={waitingOperatories}
        />
      </>
    );
  }

  // Arrived with next patient waiting - show two stacked buttons
  // Top: "Pt. Arrived" with original arrival timer (continues counting)
  // Bottom: "Next Pt." with new timer
  if (state === 'arrived_with_next') {
    const color = getTimerColor(arrivedElapsed, warningThreshold, criticalThreshold);
    const flashing = shouldFlash(arrivedElapsed, criticalThreshold);
    const critical = color === 'critical';
    let bgColor = arrivedColor;
    if (color === 'warning') bgColor = '#ef4444';
    if (color === 'critical') bgColor = '#dc2626';

    const nextColor = getNextColor();
    const nextFlashing = isNextFlashing();
    const nextCritical = isNextCritical();
    let nextBgColor = arrivedColor;
    if (nextColor === 'warning') nextBgColor = '#ef4444';
    if (nextColor === 'critical') nextBgColor = '#dc2626';
    const nextBgStyle = { backgroundColor: nextBgColor };

    if (size === 'mini') {
      return (
        <>
        <div className="flex flex-col gap-1">
          <div className="flex h-7">
            <UndoButton 
              operatoryId={operatory.id}
              operatoryNumber={operatory.number}
              onUndo={onUpdate} 
              bgColor={bgColor} 
              size={size}
            />
            <Button
              onClick={handlePatientSeated}
              data-testid={`patient-arrived-btn-${operatory.number}`}
              className={`flex-1 h-full rounded-r-lg rounded-l-none ${getButtonStyle(color, flashing, isEscalated)} text-xs transition-all duration-150 hover:brightness-110`}
              style={{ backgroundColor: bgColor }}
            >
              <div className="flex items-center justify-between w-full px-1">
                <span>Arrived</span>
                <span className="font-mono tabular-nums">{formatTime(arrivedElapsed)}</span>
              </div>
            </Button>
          </div>
          <div
            data-testid={`next-waiting-btn-${operatory.number}`}
            className={`w-full h-7 rounded-lg ${getButtonStyle(nextColor, nextFlashing, isNextEscalated)} text-xs flex items-center transition-all duration-150 hover:brightness-110`}
            style={nextBgStyle}
          >
            <div className="flex items-center justify-between w-full px-2">
              <span>Next Pt.</span>
              <div className="flex items-center gap-1">
                {nextCritical && (
                  <button
                    onClick={handleEscalateNext}
                    data-testid={`escalate-next-${operatory.number}`}
                    className={`w-4 h-4 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                      isNextEscalated ? 'ring-1 ring-red-400 scale-110' : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: '#ffffff' }}
                  >
                    <AlertCircle className={`w-3 h-3 ${isNextEscalated ? 'text-red-600' : 'text-red-500'}`} />
                  </button>
                )}
                <span className="font-mono tabular-nums">{formatTime(nextElapsed)}</span>
              </div>
            </div>
          </div>
        </div>
        <RanLongDialog 
          isOpen={showRanLongDialog}
          onClose={() => setShowRanLongDialog(false)}
          onConfirm={handleRanLongConfirm}
          waitingOperatories={waitingOperatories}
        />
        </>
      );
    }

    if (size === 'compact') {
      return (
        <div className="flex flex-col gap-1.5">
          <div className="flex h-8">
            <UndoButton 
              operatoryId={operatory.id}
              operatoryNumber={operatory.number}
              onUndo={onUpdate} 
              bgColor={bgColor} 
              size={size}
            />
            <Button
              onClick={handlePatientSeated}
              data-testid={`patient-arrived-btn-${operatory.number}`}
              className={`flex-1 h-full rounded-r-lg rounded-l-none ${getButtonStyle(color, flashing, isEscalated)} text-sm transition-all duration-150 hover:brightness-110`}
              style={{ backgroundColor: bgColor }}
            >
              <div className="flex items-center justify-between w-full">
                <span>Pt. Arrived</span>
                <span className="font-mono tabular-nums">{formatTime(arrivedElapsed)}</span>
              </div>
            </Button>
          </div>
          <div
            data-testid={`next-waiting-btn-${operatory.number}`}
            className={`w-full h-8 rounded-lg ${getButtonStyle(nextColor, nextFlashing, isNextEscalated)} text-sm flex items-center transition-all duration-150 hover:brightness-110`}
            style={nextBgStyle}
          >
            <div className="flex items-center justify-between w-full px-3">
              <span>Next Pt.</span>
              <div className="flex items-center gap-1">
                {nextCritical && (
                  <button
                    onClick={handleEscalateNext}
                    data-testid={`escalate-next-${operatory.number}`}
                    className={`w-4 h-4 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                      isNextEscalated ? 'ring-1 ring-red-400 scale-110' : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: '#ffffff' }}
                  >
                    <AlertCircle className={`w-3 h-3 ${isNextEscalated ? 'text-red-600' : 'text-red-500'}`} />
                  </button>
                )}
                <span className="font-mono tabular-nums">{formatTime(nextElapsed)}</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Normal size — two stacked buttons
    return (
      <>
      <div className={`flex flex-col gap-2`}>
        <div className={`flex gap-1 ${buttonHeight}`}>
          <div className="flex flex-1 min-w-0">
            <UndoButton 
              operatoryId={operatory.id}
              operatoryNumber={operatory.number}
              onUndo={onUpdate} 
              bgColor={bgColor} 
              size={size}
            />
            <Button
              onClick={handlePatientSeated}
              data-testid={`patient-arrived-btn-${operatory.number}`}
              className={`flex-1 h-full rounded-r-xl rounded-l-none ${getButtonStyle(color, flashing, isEscalated)} ${textSize} transition-all duration-150 hover:brightness-110`}
              style={{ backgroundColor: bgColor }}
            >
              <div className="flex items-center justify-between w-full">
                <span>Pt. Arrived</span>
                <div className="flex items-center gap-2 ml-2">
                  {critical && (
                    <button
                      onClick={handleEscalate}
                      data-testid={`escalate-arrived-${operatory.number}`}
                      className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                        isEscalated ? 'ring-2 ring-red-400 scale-110' : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: '#ffffff' }}
                    >
                      <AlertCircle className={`w-4 h-4 ${isEscalated ? 'text-red-600' : 'text-red-500'}`} />
                    </button>
                  )}
                  <span className="font-mono tabular-nums">{formatTime(arrivedElapsed)}</span>
                </div>
              </div>
            </Button>
          </div>
        </div>
        <div
          data-testid={`next-waiting-btn-${operatory.number}`}
          className={`w-full ${buttonHeight} rounded-xl ${getButtonStyle(nextColor, nextFlashing, isNextEscalated)} ${textSize} flex items-center transition-all duration-150 hover:brightness-110`}
          style={nextBgStyle}
        >
          <div className="flex items-center justify-between w-full px-4">
            <span>Next Patient Arrived</span>
            <div className="flex items-center gap-2">
              {nextCritical && (
                <button
                  onClick={handleEscalateNext}
                  data-testid={`escalate-next-${operatory.number}`}
                  className={`w-5 h-5 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                    isNextEscalated ? 'ring-2 ring-red-400 scale-110' : 'hover:scale-105'
                  }`}
                  style={{ backgroundColor: '#ffffff' }}
                >
                  <AlertCircle className={`w-4 h-4 ${isNextEscalated ? 'text-red-600' : 'text-red-500'}`} />
                </button>
              )}
              <span className="font-mono tabular-nums">{formatTime(nextElapsed)}</span>
            </div>
          </div>
        </div>
      </div>
      <RanLongDialog 
        isOpen={showRanLongDialog}
        onClose={() => setShowRanLongDialog(false)}
        onConfirm={handleRanLongConfirm}
        waitingOperatories={waitingOperatories}
      />
      </>
    );
  }


  // Seated state - show "Patient Seated" with timer + circle button for next patient
  // Click action: If dr_in_room_enabled AND doctor operatory -> go to Dr. in Room state
  //               Otherwise -> go to checkout
  if (state === 'seated') {
    // Check if next click should go to Dr. in Room state
    const goToDrInRoom = drInRoomEnabled && operatory.current_provider_name?.startsWith('Dr.');
    
    return (
      <div className={`flex gap-1 ${buttonHeight} overflow-hidden`}>
        <div className="flex flex-1 min-w-0">
          <UndoButton 
            operatoryId={operatory.id}
            operatoryNumber={operatory.number}
            onUndo={onUpdate} 
            bgColor={seatedColor} 
            size={size}
          />
          <Button
            onClick={goToDrInRoom ? handleDrInRoom : initiateCheckout}
            data-testid={`patient-seated-btn-${operatory.number}`}
            className={`flex-1 h-full rounded-r-xl rounded-l-none text-white font-semibold ${textSize} transition-all duration-150 hover:brightness-110`}
            style={{ backgroundColor: seatedColor }}
          >
            <div className="flex items-center justify-between w-full">
              <span>{size === 'mini' ? 'Seated' : 'Pt. Seated'}</span>
              <span className="font-mono tabular-nums ml-2" style={{ color: 'rgba(255,255,255,0.7)' }}>{formatTime(seatedElapsed)}</span>
            </div>
          </Button>
        </div>
        <Button
          onClick={handleNextPatientArrived}
          data-testid={`next-arrived-btn-${operatory.number}`}
          className={`h-full ${size === 'mini' ? 'w-7' : 'w-9'} shrink-0 rounded-full text-white font-semibold flex items-center justify-center transition-all duration-150 hover:brightness-110`}
          style={{ backgroundColor: arrivedColor }}
          title="Next Patient Arrived"
        >
          <User className={size === 'mini' ? 'w-3 h-3' : 'w-4 h-4'} />
        </Button>
        <RanLongDialog 
          isOpen={showRanLongDialog}
          onClose={() => setShowRanLongDialog(false)}
          onConfirm={handleRanLongConfirm}
          waitingOperatories={waitingOperatories}
        />
      </div>
    );
  }

  // Dr. in Room state - doctor is examining/treating patient
  // Shows indigo button with timer, click goes to checkout
  if (state === 'dr_in_room') {
    return (
      <div className={`flex gap-1 ${buttonHeight} overflow-hidden`}>
        <div className="flex flex-1 min-w-0">
          <UndoButton 
            operatoryId={operatory.id}
            operatoryNumber={operatory.number}
            onUndo={onUpdate} 
            bgColor={drInRoomColor} 
            size={size}
          />
          <Button
            onClick={initiateCheckout}
            data-testid={`dr-in-room-btn-${operatory.number}`}
            className={`flex-1 h-full rounded-r-xl rounded-l-none text-white font-semibold ${textSize} transition-all duration-150 hover:brightness-110`}
            style={{ backgroundColor: drInRoomColor }}
          >
            <div className="flex items-center justify-between w-full">
              <span>{size === 'mini' ? 'Dr. In' : 'Dr. in Room'}</span>
              <span className="font-mono tabular-nums ml-2" style={{ color: 'rgba(255,255,255,0.7)' }}>{formatTime(drInRoomElapsed)}</span>
            </div>
          </Button>
        </div>
        <Button
          onClick={handleNextPatientArrived}
          data-testid={`next-arrived-btn-${operatory.number}`}
          className={`h-full ${size === 'mini' ? 'w-7' : 'w-9'} shrink-0 rounded-full text-white font-semibold flex items-center justify-center transition-all duration-150 hover:brightness-110`}
          style={{ backgroundColor: arrivedColor }}
          title="Next Patient Arrived"
        >
          <User className={size === 'mini' ? 'w-3 h-3' : 'w-4 h-4'} />
        </Button>
        <RanLongDialog 
          isOpen={showRanLongDialog}
          onClose={() => setShowRanLongDialog(false)}
          onConfirm={handleRanLongConfirm}
          waitingOperatories={waitingOperatories}
        />
      </div>
    );
  }

  // Dr. in Room with next patient waiting
  if (state === 'dr_in_room_with_next') {
    const nextColor = getNextColor();
    const nextFlashing = isNextFlashing();
    const nextCritical = isNextCritical();
    // Determine background color based on timer state
    let nextBgColor = arrivedColor; // default custom arrived color
    if (nextColor === 'warning') nextBgColor = '#ef4444'; // red-500
    if (nextColor === 'critical') nextBgColor = '#dc2626'; // red-600
    const nextBgStyle = { backgroundColor: nextBgColor };
    
    // For mini/compact sizes, stack vertically
    if (size === 'mini') {
      return (
        <>
        <div className="flex flex-col gap-1">
          <Button
            onClick={initiateCheckout}
            data-testid={`dr-in-room-btn-${operatory.number}`}
            className="w-full h-7 rounded-lg text-white font-semibold text-xs transition-all duration-150 hover:scale-[1.02] hover:brightness-110"
            style={{ backgroundColor: drInRoomColor }}
          >
            <div className="flex items-center justify-between w-full px-1">
              <span>Dr. In</span>
              <span className="font-mono tabular-nums ml-2" style={{ color: 'rgba(255,255,255,0.7)' }}>{formatTime(drInRoomElapsed)}</span>
            </div>
          </Button>
          <div
            data-testid={`next-waiting-btn-${operatory.number}`}
            className={`w-full h-7 rounded-lg ${getButtonStyle(nextColor, nextFlashing, isNextEscalated)} text-xs flex items-center transition-all duration-150 hover:scale-[1.02] hover:brightness-110`}
            style={nextBgStyle}
          >
            <div className="flex items-center justify-between w-full px-2">
              <span>Next Pt.</span>
              <div className="flex items-center gap-1">
                {nextCritical && (
                  <button
                    onClick={handleEscalateNext}
                    data-testid={`escalate-next-${operatory.number}`}
                    className={`w-4 h-4 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                      isNextEscalated ? 'ring-1 ring-red-400 scale-110' : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: '#ffffff' }}
                  >
                    <AlertCircle className={`w-3 h-3 ${isNextEscalated ? 'text-red-600' : 'text-red-500'}`} />
                  </button>
                )}
                <span className="font-mono tabular-nums">{formatTime(nextElapsed)}</span>
              </div>
            </div>
          </div>
        </div>
        <RanLongDialog 
          isOpen={showRanLongDialog}
          onClose={() => setShowRanLongDialog(false)}
          onConfirm={handleRanLongConfirm}
          waitingOperatories={waitingOperatories}
        />
        </>
      );
    }
    
    if (size === 'compact') {
      return (
        <>
        <div className="flex flex-col gap-1.5">
          <Button
            onClick={initiateCheckout}
            data-testid={`dr-in-room-btn-${operatory.number}`}
            className="w-full h-8 rounded-lg text-white font-semibold text-xs transition-all duration-150 hover:scale-[1.02] hover:brightness-110"
            style={{ backgroundColor: drInRoomColor }}
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-1">
                <Stethoscope className="w-4 h-4" />
                <span>Dr. In</span>
              </div>
              <span className="font-mono tabular-nums" style={{ color: 'rgba(255,255,255,0.7)' }}>{formatTime(drInRoomElapsed)}</span>
            </div>
          </Button>
          <div
            data-testid={`next-waiting-btn-${operatory.number}`}
            className={`w-full h-8 rounded-lg ${getButtonStyle(nextColor, nextFlashing, isNextEscalated)} text-xs flex items-center transition-all duration-150 hover:scale-[1.02] hover:brightness-110`}
            style={nextBgStyle}
          >
            <div className="flex items-center justify-between w-full px-3">
              <span>Next Pt.</span>
              <div className="flex items-center gap-1">
                {nextCritical && (
                  <button
                    onClick={handleEscalateNext}
                    data-testid={`escalate-next-${operatory.number}`}
                    className={`w-4 h-4 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                      isNextEscalated ? 'ring-1 ring-red-400 scale-110' : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: '#ffffff' }}
                  >
                    <AlertCircle className={`w-3 h-3 ${isNextEscalated ? 'text-red-600' : 'text-red-500'}`} />
                  </button>
                )}
                <span className="font-mono tabular-nums">{formatTime(nextElapsed)}</span>
              </div>
            </div>
          </div>
        </div>
        <RanLongDialog 
          isOpen={showRanLongDialog}
          onClose={() => setShowRanLongDialog(false)}
          onConfirm={handleRanLongConfirm}
          waitingOperatories={waitingOperatories}
        />
        </>
      );
    }
    
    // Normal size - side by side
    return (
      <>
      <div className={`flex gap-2 ${buttonHeight}`}>
        <div className="flex flex-1">
          <UndoButton 
            operatoryId={operatory.id}
            operatoryNumber={operatory.number}
            onUndo={onUpdate} 
            bgColor={drInRoomColor} 
            size={size}
          />
          <Button
            onClick={initiateCheckout}
            data-testid={`dr-in-room-btn-${operatory.number}`}
            className={`flex-1 h-full rounded-r-xl rounded-l-none text-white font-semibold ${textSize} transition-all duration-150 hover:scale-[1.02] hover:brightness-110`}
            style={{ backgroundColor: drInRoomColor }}
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-1">
                <Stethoscope className={iconSize} />
                <span>Dr. in Room</span>
              </div>
              <span className="font-mono tabular-nums" style={{ color: 'rgba(255,255,255,0.7)' }}>{formatTime(drInRoomElapsed)}</span>
            </div>
          </Button>
        </div>
        <div
          data-testid={`next-waiting-btn-${operatory.number}`}
          className={`flex-1 h-full rounded-xl ${getButtonStyle(nextColor, nextFlashing, isNextEscalated)} ${textSize} flex items-center transition-all duration-150 hover:scale-[1.02] hover:brightness-110`}
          style={nextBgStyle}
        >
          <div className="flex items-center justify-between w-full px-4">
            <span>Next Patient Arrived</span>
            <div className="flex items-center gap-2">
              {nextCritical && (
                <button
                  onClick={handleEscalateNext}
                  data-testid={`escalate-next-${operatory.number}`}
                  className={`w-5 h-5 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                    isNextEscalated ? 'ring-2 ring-red-400 scale-110' : 'hover:scale-105'
                  }`}
                  style={{ backgroundColor: '#ffffff' }}
                >
                  <AlertCircle className={`w-4 h-4 ${isNextEscalated ? 'text-red-600' : 'text-red-500'}`} />
                </button>
              )}
              <span className="font-mono tabular-nums">{formatTime(nextElapsed)}</span>
            </div>
          </div>
        </div>
      </div>
      <RanLongDialog 
        isOpen={showRanLongDialog}
        onClose={() => setShowRanLongDialog(false)}
        onConfirm={handleRanLongConfirm}
        waitingOperatories={waitingOperatories}
      />
      </>
    );
  }

  // Seated with next patient waiting
  if (state === 'seated_with_next') {
    const nextColor = getNextColor();
    const nextFlashing = isNextFlashing();
    const nextCritical = isNextCritical();
    const goToDrInRoom = drInRoomEnabled && operatory.current_provider_name?.startsWith('Dr.');
    // Determine background color based on timer state
    let nextBgColor = arrivedColor; // default custom arrived color
    if (nextColor === 'warning') nextBgColor = '#ef4444'; // red-500
    if (nextColor === 'critical') nextBgColor = '#dc2626'; // red-600
    const nextBgStyle = { backgroundColor: nextBgColor };
    
    // For mini/compact sizes, stack vertically instead of horizontal
    if (size === 'mini') {
      return (
        <>
        <div className="flex flex-col gap-1">
          <Button
            onClick={goToDrInRoom ? handleDrInRoom : initiateCheckout}
            data-testid={`patient-seated-btn-${operatory.number}`}
            className="w-full h-7 rounded-lg text-white font-semibold text-xs transition-all duration-150 hover:scale-[1.02] hover:brightness-110"
            style={{ backgroundColor: seatedColor }}
          >
            <div className="flex items-center justify-between w-full px-1">
              <span>Seated</span>
              <span className="font-mono tabular-nums" style={{ color: 'rgba(255,255,255,0.7)' }}>{formatTime(seatedElapsed)}</span>
            </div>
          </Button>
          <div
            data-testid={`next-waiting-btn-${operatory.number}`}
            className={`w-full h-7 rounded-lg ${getButtonStyle(nextColor, nextFlashing, isNextEscalated)} text-xs flex items-center transition-all duration-150 hover:scale-[1.02] hover:brightness-110`}
            style={nextBgStyle}
          >
            <div className="flex items-center justify-between w-full px-2">
              <span>Next Pt.</span>
              <div className="flex items-center gap-1">
                {nextCritical && (
                  <button
                    onClick={handleEscalateNext}
                    data-testid={`escalate-next-${operatory.number}`}
                    className={`w-4 h-4 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                      isNextEscalated ? 'ring-1 ring-red-400 scale-110' : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: '#ffffff' }}
                  >
                    <AlertCircle className={`w-3 h-3 ${isNextEscalated ? 'text-red-600' : 'text-red-500'}`} />
                  </button>
                )}
                <span className="font-mono tabular-nums">{formatTime(nextElapsed)}</span>
              </div>
            </div>
          </div>
        </div>
        <RanLongDialog 
          isOpen={showRanLongDialog}
          onClose={() => setShowRanLongDialog(false)}
          onConfirm={handleRanLongConfirm}
          waitingOperatories={waitingOperatories}
        />
        </>
      );
    }
    
    if (size === 'compact') {
      return (
        <div className="flex flex-col gap-1.5">
          <Button
            onClick={goToDrInRoom ? handleDrInRoom : initiateCheckout}
            data-testid={`patient-seated-btn-${operatory.number}`}
            className="w-full h-8 rounded-lg text-white font-semibold text-xs transition-all duration-150 hover:scale-[1.02] hover:brightness-110"
            style={{ backgroundColor: seatedColor }}
          >
            <div className="flex items-center justify-between w-full">
              <span>Seated</span>
              <span className="font-mono tabular-nums" style={{ color: 'rgba(255,255,255,0.7)' }}>{formatTime(seatedElapsed)}</span>
            </div>
          </Button>
          <div
            data-testid={`next-waiting-btn-${operatory.number}`}
            className={`w-full h-8 rounded-lg ${getButtonStyle(nextColor, nextFlashing, isNextEscalated)} text-xs flex items-center transition-all duration-150 hover:scale-[1.02] hover:brightness-110`}
            style={nextBgStyle}
          >
            <div className="flex items-center justify-between w-full px-3">
              <span>Next Pt.</span>
              <div className="flex items-center gap-1">
                {nextCritical && (
                  <button
                    onClick={handleEscalateNext}
                    data-testid={`escalate-next-${operatory.number}`}
                    className={`w-4 h-4 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                      isNextEscalated ? 'ring-1 ring-red-400 scale-110' : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: '#ffffff' }}
                  >
                    <AlertCircle className={`w-3 h-3 ${isNextEscalated ? 'text-red-600' : 'text-red-500'}`} />
                  </button>
                )}
                <span className="font-mono tabular-nums">{formatTime(nextElapsed)}</span>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Normal size - side by side
    return (
      <>
      <div className={`flex gap-2 ${buttonHeight}`}>
        <div className="flex flex-1">
          <UndoButton 
            operatoryId={operatory.id}
            operatoryNumber={operatory.number}
            onUndo={onUpdate} 
            bgColor={seatedColor} 
            size={size}
          />
          <Button
            onClick={goToDrInRoom ? handleDrInRoom : initiateCheckout}
            data-testid={`patient-seated-btn-${operatory.number}`}
            className={`flex-1 h-full rounded-r-xl rounded-l-none text-white font-semibold ${textSize} transition-all duration-150 hover:scale-[1.02] hover:brightness-110`}
            style={{ backgroundColor: seatedColor }}
          >
            <div className="flex items-center justify-between w-full">
              <span>Patient Seated</span>
              <span className="font-mono tabular-nums" style={{ color: 'rgba(255,255,255,0.7)' }}>{formatTime(seatedElapsed)}</span>
            </div>
          </Button>
        </div>
        <div
          data-testid={`next-waiting-btn-${operatory.number}`}
          className={`flex-1 h-full rounded-xl ${getButtonStyle(nextColor, nextFlashing, isNextEscalated)} ${textSize} flex items-center transition-all duration-150 hover:scale-[1.02] hover:brightness-110`}
          style={nextBgStyle}
        >
          <div className="flex items-center justify-between w-full px-4">
            <span>Next Patient Arrived</span>
            <div className="flex items-center gap-2">
              {nextCritical && (
                <button
                  onClick={handleEscalateNext}
                  data-testid={`escalate-next-${operatory.number}`}
                  className={`w-5 h-5 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                    isNextEscalated ? 'ring-2 ring-red-400 scale-110' : 'hover:scale-105'
                  }`}
                  style={{ backgroundColor: '#ffffff' }}
                >
                  <AlertCircle className={`w-4 h-4 ${isNextEscalated ? 'text-red-600' : 'text-red-500'}`} />
                </button>
              )}
              <span className="font-mono tabular-nums">{formatTime(nextElapsed)}</span>
            </div>
          </div>
        </div>
      </div>
      <RanLongDialog 
        isOpen={showRanLongDialog}
        onClose={() => setShowRanLongDialog(false)}
        onConfirm={handleRanLongConfirm}
        waitingOperatories={waitingOperatories}
      />
      </>
    );
  }

  // Add the dialog at the end, before returning null
  return (
    <>
      <RanLongDialog 
        isOpen={showRanLongDialog}
        onClose={() => setShowRanLongDialog(false)}
        onConfirm={handleRanLongConfirm}
        waitingOperatories={waitingOperatories}
      />
    </>
  );
}

// ============ DR NEEDED BUTTON ============
function DrNeededButton({ operatory, doctors, onUpdate, size = 'normal', buttonColors = {} }) {
  const { practice } = usePractice();
  const [isOpen, setIsOpen] = useState(false);
  const [drNeededTime, setDrNeededTime] = useState(null);
  const [drInRoomTime, setDrInRoomTime] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [drInRoomElapsed, setDrInRoomElapsed] = useState(0);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [messageMode, setMessageMode] = useState(null); // null or doctor object
  const [message, setMessage] = useState('');
  const [npSelectedDoctor, setNpSelectedDoctor] = useState(null); // Track which doctor has NP enabled (by ID)
  const [showDoctorSelectDialog, setShowDoctorSelectDialog] = useState(false); // Dialog for selecting which doctor arrived
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const messageInputRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  const warningThreshold = practice?.warning_threshold || 300;
  const criticalThreshold = practice?.critical_threshold || 420;
  const drInRoomEnabled = practice?.dr_in_room_enabled || false;
  
  // Get custom colors from props or use defaults
  const drNeededColor = buttonColors.dr_needed || '#f59e0b'; // amber-500
  const drInRoomColor = buttonColors.dr_in_room || '#4f46e5'; // indigo-600
  
  // Use escalation state from operatory (shared with AlertStack)
  const isEscalated = operatory.dr_needed_escalated || false;
  
  // Dr in room state for Dr Needed button sequence
  const isDrInRoom = !!operatory.dr_needed_in_room_at;

  // Handle mouse enter/leave for auto-close with tolerance
  const handleMouseEnterMenu = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleMouseLeaveMenu = () => {
    // Don't auto-close if in message mode
    if (messageMode) return;
    
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 300); // 300ms delay before closing
  };

  const handleMouseEnterButton = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleMouseLeaveButton = () => {
    // Only start close timer if menu is open and not in message mode
    if (isOpen && !messageMode) {
      closeTimeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, 300);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Size-based styling
  const getButtonHeight = () => {
    switch (size) {
      case 'compact': return 'h-10';
      case 'mini': return 'h-8';
      default: return 'h-12';
    }
  };

  const getTextSize = () => {
    switch (size) {
      case 'compact': return 'text-sm';
      case 'mini': return 'text-xs';
      default: return 'text-base';
    }
  };

  const getIconSize = () => {
    switch (size) {
      case 'compact': return 'w-3.5 h-3.5';
      case 'mini': return 'w-3 h-3';
      default: return 'w-4 h-4';
    }
  };

  const buttonHeight = getButtonHeight();
  const textSize = getTextSize();
  const iconSize = getIconSize();

  // Focus message input when entering message mode
  useEffect(() => {
    if (messageMode && messageInputRef.current) {
      messageInputRef.current.focus();
    }
  }, [messageMode]);

  useEffect(() => {
    if (operatory.dr_needed_at) {
      const t = new Date(operatory.dr_needed_at);
      setDrNeededTime(t);
      setElapsed(Math.floor((Date.now() - t.getTime()) / 1000));
      setSelectedDoctor(operatory.dr_needed_name || 'Any Doctor');
    } else {
      setDrNeededTime(null);
      setSelectedDoctor(null);
      setElapsed(0);
    }
    if (operatory.dr_needed_in_room_at) {
      const t = new Date(operatory.dr_needed_in_room_at);
      setDrInRoomTime(t);
      setDrInRoomElapsed(Math.floor((Date.now() - t.getTime()) / 1000));
    } else {
      setDrInRoomTime(null);
      setDrInRoomElapsed(0);
    }
  }, [operatory]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (drNeededTime && !isDrInRoom) {
        setElapsed(Math.floor((Date.now() - drNeededTime.getTime()) / 1000));
      }
      if (drInRoomTime && isDrInRoom) {
        setDrInRoomElapsed(Math.floor((Date.now() - drInRoomTime.getTime()) / 1000));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [drNeededTime, drInRoomTime, isDrInRoom]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) &&
          buttonRef.current && !buttonRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleDrNeeded = async (doctorName = null, doctorId = null, alertMessage = null) => {
    // Check if this specific doctor has NP enabled
    const isNewPatient = npSelectedDoctor === doctorId;
    // Optimistic reset — prevent stale timer flash
    setDrNeededTime(new Date());
    setElapsed(0);
    setSelectedDoctor(doctorName || 'Any Doctor');
    try {
      await axios.post(`${API}/operatory/${operatory.id}/dr-needed`, {
        doctor_name: doctorName,
        doctor_id: doctorId,
        message: alertMessage,
        device_id: getDeviceId(),
        is_new_patient: isNewPatient
      });
      const successMsg = alertMessage 
        ? `Message sent to ${doctorName}` 
        : (doctorName ? `${doctorName} needed${isNewPatient ? ' (NP)' : ''}` : 'Doctor needed');
      toast.success(successMsg);
      setIsOpen(false);
      setMessageMode(null);
      setMessage('');
      setNpSelectedDoctor(null); // Reset NP selection after sending
      onUpdate();
    } catch (error) {
      toast.error('Failed to request doctor');
    }
  };

  const handleSendMessage = (doc) => {
    if (message.trim()) {
      handleDrNeeded(formatDoctorName(doc), doc.id, message.trim());
    }
  };

  const handleCancelMessage = () => {
    setMessageMode(null);
    setMessage('');
  };

  const handleClearDrNeeded = async () => {
    // Optimistic reset — prevent stale timer flash
    setDrNeededTime(null);
    setSelectedDoctor(null);
    setElapsed(0);
    setDrInRoomTime(null);
    setDrInRoomElapsed(0);
    try {
      await axios.post(`${API}/operatory/${operatory.id}/clear-dr-needed`);
      toast.success('Doctor request cleared');
      onUpdate();
    } catch (error) {
      toast.error('Failed to clear request');
    }
  };

  // New handler for Dr. in Room state (when dr_in_room_enabled)
  // Check if we need to prompt for doctor selection
  const needsDoctorSelection = () => {
    // If operatory is assigned to a hygienist (not a doctor) AND dr_needed was requested without specifying a doctor
    const isHygienistOperatory = operatory.current_provider_name && !operatory.current_provider_name.startsWith('Dr.');
    const isAnyDoctor = !operatory.dr_needed_name || operatory.dr_needed_name === 'Any Doctor';
    return isHygienistOperatory && isAnyDoctor;
  };

  const handleDrNeededInRoom = async (selectedDoctorId = null, selectedDoctorName = null) => {
    // Optimistic reset — prevent stale timer flash
    setDrInRoomTime(new Date());
    setDrInRoomElapsed(0);
    try {
      await axios.post(`${API}/operatory/${operatory.id}/dr-needed-in-room`, {
        doctor_id: selectedDoctorId,
        doctor_name: selectedDoctorName
      });
      toast.success(selectedDoctorName ? `${selectedDoctorName} in room` : 'Doctor in room');
      setShowDoctorSelectDialog(false);
      onUpdate();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Wrapper to check if we need doctor selection first
  const initiateDrInRoom = () => {
    if (needsDoctorSelection()) {
      setShowDoctorSelectDialog(true);
    } else {
      handleDrNeededInRoom();
    }
  };

  // Handler for completing the Dr. Needed sequence (after Dr. in Room)
  const handleDrNeededComplete = async () => {
    // Optimistic reset — prevent stale timer flash
    setDrNeededTime(null);
    setSelectedDoctor(null);
    setElapsed(0);
    setDrInRoomTime(null);
    setDrInRoomElapsed(0);
    try {
      await axios.post(`${API}/operatory/${operatory.id}/dr-needed-complete`);
      toast.success('Doctor request complete');
      onUpdate();
    } catch (error) {
      toast.error('Failed to complete request');
    }
  };

  const timerColor = getTimerColor(elapsed, warningThreshold, criticalThreshold);
  const isFlashing = shouldFlash(elapsed, criticalThreshold);
  const isCritical = timerColor === 'critical';

  // Format doctor name with preference
  const formatDoctorName = (doc) => {
    const preference = doc.dr_display_preference || 'last';
    const displayName = preference === 'first' ? getFirstName(doc) : getLastName(doc);
    return `Dr. ${displayName || doc.full_name}`;
  };

  const handleEscalate = async (e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/operatory/${operatory.id}/toggle-escalation`, {
        alert_type: 'dr_needed'
      });
      onUpdate();
    } catch (error) {
      console.error('Failed to toggle escalation:', error);
    }
  };

  // Returns CSS classes for animations and borders - NO background classes
  const getButtonStyle = () => {
    let base = 'text-white font-semibold ';
    if (!drNeededTime) return base;
    if (timerColor === 'critical') {
      if (isEscalated) {
        return base + 'animate-pulse-escalated border-2 border-red-300 shadow-lg shadow-red-500/50';
      }
      return base + 'animate-pulse-subtle border border-red-400';
    }
    return base;
  };
  
  // Get background color based on timer state - always returns a color, never black/white
  const getDrNeededBgStyle = () => {
    if (!drNeededTime) return { backgroundColor: drNeededColor };
    if (timerColor === 'critical') return { backgroundColor: '#dc2626' }; // red-600
    if (timerColor === 'warning') return { backgroundColor: '#ef4444' }; // red-500
    return { backgroundColor: drNeededColor };
  };

  const [menuStyle, setMenuStyle] = useState({});
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const menuWidth = Math.max(rect.width, 220);
      const estimatedMenuHeight = 300; // Estimate menu height
      
      // Calculate left position - ensure it stays within viewport
      let left = rect.left;
      if (left + menuWidth > viewportWidth - 10) {
        left = viewportWidth - menuWidth - 10;
      }
      if (left < 10) {
        left = 10;
      }
      
      // Calculate top position - in contact with button (no gap), flip if needed
      let top = rect.bottom; // No gap - in contact with button
      let maxHeight = viewportHeight - top - 20;
      
      if (top + estimatedMenuHeight > viewportHeight - 20) {
        // Not enough space below, try above
        const spaceAbove = rect.top - 20;
        if (spaceAbove > maxHeight) {
          // More space above, flip the menu (in contact with top of button)
          top = rect.top - Math.min(estimatedMenuHeight, spaceAbove);
          maxHeight = spaceAbove;
        }
      }
      
      setMenuStyle({
        position: 'fixed',
        left: `${left}px`,
        top: `${top}px`,
        minWidth: `${menuWidth}px`,
        maxHeight: `${Math.min(maxHeight, 400)}px`,
        zIndex: 99999,
      });
    }
  }, [isOpen, messageMode]);

  const menu = isOpen ? createPortal(
    <div 
      ref={menuRef} 
      style={menuStyle} 
      className="bg-white rounded-lg shadow-xl border-2 border-slate-200 flex flex-col overflow-hidden"
      onMouseEnter={handleMouseEnterMenu}
      onMouseLeave={handleMouseLeaveMenu}
    >
      {messageMode ? (
        // Message input mode
        <div className="px-3 py-2">
          <div className="flex items-center gap-2 mb-2 text-sm font-medium text-slate-700">
            <Mail className="w-4 h-4 text-amber-500" />
            <span>Message to {formatDoctorName(messageMode)}</span>
          </div>
          <textarea
            ref={messageInputRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your message..."
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
            rows={3}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage(messageMode);
              }
              if (e.key === 'Escape') {
                handleCancelMessage();
              }
            }}
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={handleCancelMessage}
              className="flex-1 px-3 py-1.5 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => handleSendMessage(messageMode)}
              disabled={!message.trim()}
              className="flex-1 px-3 py-1.5 text-sm font-medium text-white bg-amber-500 hover:bg-amber-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
            >
              <Send className="w-3.5 h-3.5" />
              Send
            </button>
          </div>
        </div>
      ) : (
        // Doctor list mode
        <>
          {/* Header */}
          <div className="px-3 py-2 border-b border-slate-200 flex-shrink-0">
            <span className="text-sm font-semibold text-slate-700">Dr. Needed</span>
          </div>
          {/* Scrollable doctor list */}
          <div className="overflow-y-auto flex-1 py-1">
            {doctors.map(doc => (
              <div
                key={doc.id}
                className="flex items-center justify-between px-2 py-1 hover:bg-dp-50 group"
              >
              <button
                onClick={() => handleDrNeeded(formatDoctorName(doc), doc.id)}
                className="flex-1 px-2 py-1.5 text-left text-sm font-medium text-slate-800 hover:text-dp-700 rounded transition-colors"
                data-testid={`dr-option-${doc.id}`}
              >
                {formatDoctorName(doc)}
              </button>
              <div className="flex items-center gap-1">
                {/* NP indicator for this specific doctor */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Toggle NP for this specific doctor only
                    setNpSelectedDoctor(npSelectedDoctor === doc.id ? null : doc.id);
                  }}
                  className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${
                    npSelectedDoctor === doc.id 
                      ? 'bg-amber-500 text-white' 
                      : 'text-slate-400 hover:text-amber-500 hover:bg-amber-50'
                  }`}
                  title={npSelectedDoctor === doc.id ? 'New Patient exam enabled (click to disable)' : 'Mark as New Patient exam'}
                  data-testid={`dr-np-${doc.id}`}
                >
                  <span className="text-xs font-bold">NP</span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setMessageMode(doc);
                  }}
                  className="p-1.5 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded transition-colors"
                  title={`Send message to ${formatDoctorName(doc)}`}
                  data-testid={`dr-message-${doc.id}`}
                >
                  <Mail className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          </div>
          {/* Any Doctor option - fixed at bottom */}
          <div className="border-t border-slate-200 flex-shrink-0 py-1">
            <button
              onClick={() => handleDrNeeded('Any Doctor', null)}
              className="w-full px-4 py-2 text-left text-sm font-medium text-amber-600 hover:bg-amber-50 transition-colors"
              data-testid="dr-option-any"
            >
              Any Doctor
            </button>
          </div>
        </>
      )}
    </div>,
    document.body
  ) : null;

  // Determine button action and appearance based on current state
  const getMainButtonAction = () => {
    if (isDrInRoom) {
      // Dr is in room, clicking completes the request
      return handleDrNeededComplete;
    }
    if (drNeededTime && drInRoomEnabled) {
      // Dr needed is active and dr_in_room feature enabled, clicking sets Dr in Room
      // Use initiateDrInRoom to check if doctor selection is needed
      return initiateDrInRoom;
    }
    // Default: clear the request
    return handleClearDrNeeded;
  };

  const getDrInRoomButtonStyle = () => {
    return 'text-white';
  };
  
  const getDrInRoomBgStyle = () => {
    return { backgroundColor: drInRoomColor };
  };

  // Doctor Selection Dialog for "Any Doctor" requests
  const doctorSelectDialog = showDoctorSelectDialog ? (
    <Dialog open={showDoctorSelectDialog} onOpenChange={setShowDoctorSelectDialog}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-indigo-600" />
            Which Doctor Arrived?
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-sm text-slate-600 mb-4">
            Select the doctor who arrived for {operatory.current_provider_name}'s patient in {operatory.name}:
          </p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {doctors.map(doc => (
              <button
                key={doc.id}
                onClick={() => handleDrNeededInRoom(doc.id, formatDoctorName(doc))}
                className="w-full px-4 py-3 text-left rounded-lg border border-slate-200 hover:bg-indigo-50 hover:border-indigo-300 transition-colors flex items-center gap-3"
                data-testid={`select-doctor-${doc.id}`}
              >
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                  <User className="w-4 h-4 text-indigo-600" />
                </div>
                <span className="font-medium text-slate-800">{formatDoctorName(doc)}</span>
              </button>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setShowDoctorSelectDialog(false)}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  ) : null;

  return (
    <div className="relative">
      <div 
        ref={buttonRef} 
        className={`flex ${buttonHeight}`}
        onMouseEnter={handleMouseEnterButton}
        onMouseLeave={handleMouseLeaveButton}
      >
        {isDrInRoom ? (
          // Dr. in Room state - show indigo button with timer
          <>
            <Button
              onClick={handleDrNeededComplete}
              data-testid={`dr-in-room-complete-btn-${operatory.number}`}
              className={`flex-1 h-full rounded-l-xl rounded-r-none ${getDrInRoomButtonStyle()} ${textSize} overflow-hidden transition-all duration-150 hover:brightness-110`}
              style={getDrInRoomBgStyle()}
            >
              <div className="flex items-center justify-between w-full min-w-0">
                <div className="flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
                  <Stethoscope className={`${iconSize} flex-shrink-0`} />
                  <span className="truncate text-left">{size === 'mini' ? 'Dr. In' : 'Dr. in Room'}</span>
                </div>
                <span className="font-mono tabular-nums flex-shrink-0 ml-1" style={{ color: 'rgba(255,255,255,0.7)' }}>{formatTime(drInRoomElapsed)}</span>
              </div>
            </Button>
            <Button
              onClick={() => setIsOpen(!isOpen)}
              className={`${size === 'mini' ? 'w-7' : 'w-10'} h-full rounded-r-xl rounded-l-none border-l border-white/20 ${getDrInRoomButtonStyle()} flex-shrink-0 transition-all duration-150 hover:brightness-110`}
              style={getDrInRoomBgStyle()}
            >
              <ChevronDown className={iconSize} />
            </Button>
          </>
        ) : drNeededTime ? (
          // Dr. Needed active state - show timer with option to set Dr in Room (if enabled)
          <>
            <UndoButton 
              operatoryId={operatory.id}
              operatoryNumber={operatory.number}
              onUndo={onUpdate} 
              bgColor={getDrNeededBgStyle().backgroundColor || drNeededColor} 
              size={size}
              undoEndpoint={`${API}/operatory/${operatory.id}/clear-dr-needed`}
            />
            <Button
              onClick={drInRoomEnabled ? initiateDrInRoom : handleClearDrNeeded}
              data-testid={drInRoomEnabled ? `dr-needed-in-room-btn-${operatory.number}` : `dr-needed-clear-btn-${operatory.number}`}
              className={`flex-1 h-full rounded-none ${getButtonStyle()} ${textSize} overflow-hidden transition-all duration-150 hover:brightness-110`}
              style={getDrNeededBgStyle()}
            >
              <div className="flex items-center justify-between w-full min-w-0">
                <div className="flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
                  <Stethoscope className={`${iconSize} flex-shrink-0`} />
                  <span className="truncate text-left">{size === 'mini' ? 'Dr.' : selectedDoctor}</span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                  {isCritical && !drInRoomEnabled && (
                    <button
                      onClick={handleEscalate}
                      data-testid={`escalate-dr-needed-${operatory.number}`}
                      className={`${size === 'mini' ? 'w-4 h-4' : 'w-5 h-5'} rounded-full flex items-center justify-center transition-all ${
                        isEscalated ? 'ring-2 ring-red-400 scale-110' : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: '#ffffff' }}
                    >
                      <AlertCircle className={`${size === 'mini' ? 'w-3 h-3' : 'w-4 h-4'} ${isEscalated ? 'text-red-600' : 'text-red-500'}`} />
                    </button>
                  )}
                  <span className="font-mono tabular-nums">{formatTime(elapsed)}</span>
                </div>
              </div>
            </Button>
            <Button
              onClick={() => setIsOpen(!isOpen)}
              className={`${size === 'mini' ? 'w-7' : 'w-10'} h-full rounded-r-xl rounded-l-none border-l border-white/20 ${getButtonStyle()} flex-shrink-0 transition-all duration-150 hover:brightness-110`}
              style={getDrNeededBgStyle()}
            >
              <ChevronDown className={iconSize} />
            </Button>
          </>
        ) : (
          // Initial state - Dr. Needed button
          <>
            <Button
              onClick={() => handleDrNeeded()}
              data-testid={`dr-needed-btn-${operatory.number}`}
              className={`flex-1 h-full rounded-l-xl rounded-r-none ${getButtonStyle()} ${textSize} transition-all duration-150 hover:brightness-110`}
              style={getDrNeededBgStyle()}
            >
              <div className="flex items-center gap-1">
                <Stethoscope className={iconSize} />
                <span>{size === 'mini' ? 'Dr.' : 'Dr. Needed'}</span>
              </div>
            </Button>
            <Button
              onClick={() => setIsOpen(!isOpen)}
              data-testid={`dr-needed-dropdown-${operatory.number}`}
              className={`${size === 'mini' ? 'w-7' : 'w-10'} h-full rounded-r-xl rounded-l-none border-l border-white/20 ${getButtonStyle()} transition-all duration-150 hover:brightness-110`}
              style={getDrNeededBgStyle()}
            >
              <ChevronDown className={iconSize} />
            </Button>
          </>
        )}
      </div>
      {menu}
      {doctorSelectDialog}
    </div>
  );
}

// ============ HELP NEEDED BUTTON ============
function HelpNeededButton({ operatory, onUpdate, size = 'normal', buttonColors = {} }) {
  const { practice } = usePractice();
  const [isOpen, setIsOpen] = useState(false);
  const [helpNeededTime, setHelpNeededTime] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [helpType, setHelpType] = useState(null);
  const [runningBehindMinutes, setRunningBehindMinutes] = useState(5);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  const warningThreshold = practice?.warning_threshold || 300;
  const criticalThreshold = practice?.critical_threshold || 420;
  
  // Get custom color from props or use default
  const helpNeededColor = buttonColors.help_needed || '#8b5cf6'; // violet-500
  
  // Use escalation state from operatory (shared with AlertStack)
  const isEscalated = operatory.help_needed_escalated || false;

  // Handle mouse enter/leave for auto-close with tolerance
  const handleMouseEnterMenu = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleMouseLeaveMenu = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 300); // 300ms delay before closing
  };

  const handleMouseEnterButton = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleMouseLeaveButton = () => {
    if (isOpen) {
      closeTimeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, 300);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Size-based styling
  const getButtonHeight = () => {
    switch (size) {
      case 'compact': return 'h-10';
      case 'mini': return 'h-8';
      default: return 'h-12';
    }
  };

  const getTextSize = () => {
    switch (size) {
      case 'compact': return 'text-sm';
      case 'mini': return 'text-xs';
      default: return 'text-base';
    }
  };

  const getIconSize = () => {
    switch (size) {
      case 'compact': return 'w-3.5 h-3.5';
      case 'mini': return 'w-3 h-3';
      default: return 'w-4 h-4';
    }
  };

  const buttonHeight = getButtonHeight();
  const textSize = getTextSize();
  const iconSize = getIconSize();

  useEffect(() => {
    if (operatory.help_needed_at) {
      const t = new Date(operatory.help_needed_at);
      setHelpNeededTime(t);
      setElapsed(Math.floor((Date.now() - t.getTime()) / 1000));
      setHelpType(operatory.help_type || 'General');
    } else {
      setHelpNeededTime(null);
      setHelpType(null);
      setElapsed(0);
    }
  }, [operatory]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (helpNeededTime) {
        setElapsed(Math.floor((Date.now() - helpNeededTime.getTime()) / 1000));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [helpNeededTime]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) &&
          buttonRef.current && !buttonRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleHelpNeeded = async (type = 'Help Needed') => {
    // Optimistic reset — prevent stale timer flash
    setHelpNeededTime(new Date());
    setHelpType(type);
    setElapsed(0);
    try {
      await axios.post(`${API}/operatory/${operatory.id}/help-needed`, {
        help_type: type,
        device_id: getDeviceId()
      });
      toast.success(type === 'Help Needed' ? 'Help needed' : `${type} help needed`);
      setIsOpen(false);
      onUpdate();
    } catch (error) {
      toast.error('Failed to request help');
    }
  };

  const handleClearHelp = async () => {
    // Optimistic reset — prevent stale timer flash
    setHelpNeededTime(null);
    setHelpType(null);
    setElapsed(0);
    try {
      await axios.post(`${API}/operatory/${operatory.id}/clear-help`);
      toast.success('Help request cleared');
      onUpdate();
    } catch (error) {
      toast.error('Failed to clear request');
    }
  };

  const timerColor = getTimerColor(elapsed, warningThreshold, criticalThreshold);
  const isFlashing = shouldFlash(elapsed, criticalThreshold);
  const isCritical = timerColor === 'critical';

  const handleEscalate = async (e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/operatory/${operatory.id}/toggle-escalation`, {
        alert_type: 'help_needed'
      });
      onUpdate();
    } catch (error) {
      console.error('Failed to toggle escalation:', error);
    }
  };

  // Returns CSS classes for animations and borders - NO background classes
  const getButtonStyle = () => {
    let base = 'text-white font-semibold ';
    if (!helpNeededTime) return base;
    if (timerColor === 'critical') {
      if (isEscalated) {
        return base + 'animate-pulse-escalated border-2 border-red-300 shadow-lg shadow-red-500/50';
      }
      return base + 'animate-pulse-subtle border border-red-400';
    }
    return base;
  };
  
  // Get background color based on timer state - always returns a color, never black/white
  const getHelpBgStyle = () => {
    if (!helpNeededTime) return { backgroundColor: helpNeededColor };
    if (timerColor === 'critical') return { backgroundColor: '#dc2626' }; // red-600
    if (timerColor === 'warning') return { backgroundColor: '#ef4444' }; // red-500
    return { backgroundColor: helpNeededColor };
  };

  const [menuStyle, setMenuStyle] = useState({});
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const menuWidth = Math.max(rect.width, 220);
      const estimatedMenuHeight = 250;
      
      // Calculate left position
      let left = rect.left;
      if (left + menuWidth > viewportWidth - 10) {
        left = viewportWidth - menuWidth - 10;
      }
      if (left < 10) {
        left = 10;
      }
      
      // Calculate top position - in contact with button, flip if needed
      let top = rect.bottom;
      let maxHeight = viewportHeight - top - 20;
      
      if (top + estimatedMenuHeight > viewportHeight - 20) {
        const spaceAbove = rect.top - 20;
        if (spaceAbove > maxHeight) {
          top = rect.top - Math.min(estimatedMenuHeight, spaceAbove);
          maxHeight = spaceAbove;
        }
      }
      
      setMenuStyle({
        position: 'fixed',
        left: `${left}px`,
        top: `${top}px`,
        minWidth: `${menuWidth}px`,
        maxHeight: `${Math.min(maxHeight, 350)}px`,
        zIndex: 99999,
      });
    }
  }, [isOpen]);

  const handleScrollMinutes = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? 5 : -5;
    setRunningBehindMinutes(prev => Math.max(5, Math.min(60, prev + delta)));
  };

  const menu = isOpen ? createPortal(
    <div 
      ref={menuRef} 
      style={menuStyle} 
      className="bg-white rounded-lg shadow-xl border-2 border-slate-200 py-2 overflow-y-auto"
      onMouseEnter={handleMouseEnterMenu}
      onMouseLeave={handleMouseLeaveMenu}
    >
      {/* Running Behind Option */}
      <div className="px-4 py-2 hover:bg-purple-50 flex items-center gap-2">
        <span className="text-sm font-medium text-slate-800">Running behind</span>
        <div 
          onWheel={handleScrollMinutes}
          onClick={() => handleHelpNeeded(`Running ${runningBehindMinutes} min behind`)}
          className="w-10 h-7 bg-slate-100 hover:bg-purple-100 border border-slate-300 rounded flex items-center justify-center cursor-pointer transition-colors"
          title="Scroll to adjust, click to send"
        >
          <span className="text-sm font-bold text-purple-600">{runningBehindMinutes}</span>
        </div>
        <span className="text-sm text-slate-600">min</span>
      </div>
      
      {/* Can you seat next patient */}
      <button
        onClick={() => handleHelpNeeded('Can you seat next patient?')}
        className="w-full px-4 py-2 text-left hover:bg-purple-100 text-sm font-medium text-slate-800"
      >
        Can you seat next patient?
      </button>
      
      {/* Treatment Planned */}
      <button
        onClick={() => handleHelpNeeded('Treatment Planned - Present financials')}
        className="w-full px-4 py-2 text-left hover:bg-purple-100 text-sm font-medium text-slate-800"
      >
        Treatment Planned. Present financials at checkout
      </button>
      
      <div className="border-t border-slate-200 my-1"></div>
      
      {/* Original options */}
      <button
        onClick={() => handleHelpNeeded('X-Ray')}
        className="w-full px-4 py-2 text-left hover:bg-purple-100 text-sm font-medium text-slate-800"
      >
        X-Ray
      </button>
      <button
        onClick={() => handleHelpNeeded('Perio Chart')}
        className="w-full px-4 py-2 text-left hover:bg-purple-100 text-sm font-medium text-slate-800"
      >
        Perio Chart
      </button>
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative">
      <div 
        ref={buttonRef} 
        className={`flex ${buttonHeight}`}
        onMouseEnter={handleMouseEnterButton}
        onMouseLeave={handleMouseLeaveButton}
      >
        {helpNeededTime ? (
          <>
            <UndoButton 
              operatoryId={operatory.id}
              operatoryNumber={operatory.number}
              onUndo={onUpdate} 
              bgColor={getHelpBgStyle().backgroundColor || helpColor} 
              size={size}
              undoEndpoint={`${API}/operatory/${operatory.id}/clear-help`}
            />
            <Button
              onClick={handleClearHelp}
              className={`flex-1 h-full rounded-none ${getButtonStyle()} ${textSize} overflow-hidden transition-all duration-150 hover:brightness-110`}
              style={getHelpBgStyle()}
            >
              <div className="flex items-center justify-between w-full min-w-0">
                <div className="flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
                  <HelpCircle className={`${iconSize} flex-shrink-0`} />
                  <span className="truncate text-left" style={{ fontSize: helpType && helpType.length > 15 ? (size === 'mini' ? '0.65rem' : size === 'compact' ? '0.7rem' : '0.75rem') : undefined }}>
                    {size === 'mini' ? 'Help' : (helpType === 'General' ? 'Help Needed' : helpType)}
                  </span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                  {isCritical && (
                    <div
                      onClick={handleEscalate}
                      data-testid={`escalate-help-${operatory.number}`}
                      className={`${size === 'mini' ? 'w-4 h-4' : 'w-5 h-5'} rounded-full flex items-center justify-center transition-all cursor-pointer ${
                        isEscalated ? 'ring-2 ring-red-400 scale-110' : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: '#ffffff' }}
                    >
                      <AlertCircle className={`${size === 'mini' ? 'w-3 h-3' : 'w-4 h-4'} ${isEscalated ? 'text-red-600' : 'text-red-500'}`} />
                    </div>
                  )}
                  <span className="font-mono tabular-nums" style={{ fontSize: helpType && helpType.length > 15 ? (size === 'mini' ? '0.65rem' : size === 'compact' ? '0.7rem' : '0.75rem') : undefined }}>
                    {formatTime(elapsed)}
                  </span>
                </div>
              </div>
            </Button>
            <Button
              onClick={() => setIsOpen(!isOpen)}
              className={`${size === 'mini' ? 'w-7' : 'w-10'} h-full rounded-r-xl rounded-l-none border-l border-white/20 ${getButtonStyle()} flex-shrink-0 transition-all duration-150 hover:brightness-110`}
              style={getHelpBgStyle()}
            >
              <ChevronDown className={iconSize} />
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={() => handleHelpNeeded()}
              data-testid={`help-needed-btn-${operatory.number}`}
              className={`flex-1 h-full rounded-l-xl rounded-r-none ${getButtonStyle()} ${textSize} transition-all duration-150 hover:brightness-110`}
              style={getHelpBgStyle()}
            >
              <div className="flex items-center gap-1">
                <HelpCircle className={iconSize} />
                <span>{size === 'mini' ? 'Help' : 'Help Needed'}</span>
              </div>
            </Button>
            <Button
              onClick={() => setIsOpen(!isOpen)}
              data-testid={`help-needed-dropdown-${operatory.number}`}
              className={`${size === 'mini' ? 'w-7' : 'w-10'} h-full rounded-r-xl rounded-l-none border-l border-white/20 ${getButtonStyle()} transition-all duration-150 hover:brightness-110`}
              style={getHelpBgStyle()}
            >
              <ChevronDown className={iconSize} />
            </Button>
          </>
        )}
      </div>
      {menu}
    </div>
  );
}

// ============ PROVIDER SELECTOR ============
// ============ PROVIDER SELECTOR ============
function ProviderSelector({ operatory, onUpdate, isRegisteredDevice, hygienistNameMap = {}, size = 'normal' }) {
  const [providers, setProviders] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [currentProvider, setCurrentProvider] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const { darkMode } = useTheme();

  // Size-based styling
  const getTitleSize = () => {
    switch (size) {
      case 'compact': return 'text-base';
      case 'mini': return 'text-sm';
      default: return 'text-lg';
    }
  };

  const getSubtitleSize = () => {
    switch (size) {
      case 'mini': return 'text-[10px]';
      default: return 'text-xs';
    }
  };

  const getIconSize = () => {
    switch (size) {
      case 'compact': return 'w-3 h-3';
      case 'mini': return 'w-2.5 h-2.5';
      default: return 'w-3.5 h-3.5';
    }
  };

  const titleSize = getTitleSize();
  const subtitleSize = getSubtitleSize();
  const iconSize = getIconSize();

  // Helper to format provider name with Dr. prefix if they're a doctor, 
  // first name only for hygienists (with last initial if disambiguation needed)
  const formatProviderName = (provider) => {
    if (!provider) return '';
    const isDoctor = provider.role === 'doctor' || provider.base_role === 'doctor';
    const isHygienist = provider.role === 'hygienist' || provider.base_role === 'hygienist';
    
    if (isDoctor) {
      const preference = provider.dr_display_preference || 'last';
      const displayName = preference === 'first' ? getFirstName(provider) : getLastName(provider);
      return `Dr. ${displayName || provider.full_name}`;
    }
    
    if (isHygienist) {
      // Use hygienist name map for disambiguation
      const hygienistInfo = hygienistNameMap[provider.id];
      if (hygienistInfo && hygienistInfo.display_name) {
        return hygienistInfo.display_name;
      }
      // Fallback to first name only
      return getFirstName(provider) || provider.full_name;
    }
    
    return provider.full_name || `${provider.first_name || ''} ${provider.last_name || ''}`.trim();
  };

  // Load current provider from operatory
  useEffect(() => {
    if (operatory.current_provider_id) {
      setCurrentProvider({
        id: operatory.current_provider_id,
        name: operatory.current_provider_name // This is already formatted when saved
      });
    } else {
      setCurrentProvider(null);
    }
  }, [operatory]);

  // Fetch providers assigned to this operatory
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const res = await axios.get(`${API}/operatories/${operatory.id}/providers`);
        setProviders(res.data);
      } catch (error) {
        console.error('Failed to fetch providers:', error);
      }
    };
    fetchProviders();
  }, [operatory.id]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) &&
          buttonRef.current && !buttonRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelectProvider = async (provider) => {
    try {
      const formattedName = provider ? formatProviderName(provider) : '';
      await axios.put(`${API}/operatories/${operatory.id}/current-provider`, {
        provider_id: provider?.id || null,
        provider_name: formattedName
      });
      setCurrentProvider(provider ? { id: provider.id, name: formattedName } : null);
      setIsOpen(false);
      toast.success(provider ? `Provider set to ${formattedName}` : 'Provider cleared');
      onUpdate();
    } catch (error) {
      toast.error('Failed to set provider');
    }
  };

  const handleRegisterDevice = async () => {
    try {
      const deviceId = getDeviceId();
      const deviceName = getDeviceName();
      
      await axios.post(`${API}/devices/register`, {
        device_id: deviceId,
        device_name: deviceName,
        operatory_id: operatory.id
      });
      
      setIsOpen(false);
      toast.success(`This device is now registered to ${operatory.name}`);
      onUpdate();
    } catch (error) {
      toast.error('Failed to register device');
    }
  };

  const [menuStyle, setMenuStyle] = useState({});
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuStyle({
        position: 'fixed',
        left: `${rect.left}px`,
        top: `${rect.bottom + 4}px`,
        minWidth: `${rect.width}px`,
        zIndex: 99999,
      });
    }
  }, [isOpen]);

  const menu = isOpen ? createPortal(
    <div ref={menuRef} style={menuStyle} className="bg-white rounded-lg shadow-xl border-2 border-slate-200 py-2 min-w-[180px]">
      {/* Option to show operatory name (clear provider) */}
      {currentProvider && (
        <button
          onClick={() => handleSelectProvider(null)}
          className="w-full px-4 py-2 text-left hover:bg-slate-100 text-sm font-medium text-slate-600 border-b border-slate-100"
        >
          {operatory.name}
        </button>
      )}
      
      {providers.length === 0 ? (
        <div className="px-4 py-2 text-sm text-slate-500 italic">
          No providers assigned
        </div>
      ) : (
        providers.map(provider => (
          <button
            key={provider.id}
            onClick={() => handleSelectProvider(provider)}
            className={`w-full px-4 py-2 text-left hover:bg-dp-100 text-sm font-medium ${
              currentProvider?.id === provider.id ? 'bg-dp-50 text-dp-700' : 'text-slate-800'
            }`}
          >
            {formatProviderName(provider)}
          </button>
        ))
      )}
      
      {/* Register device option */}
      <button
        onClick={handleRegisterDevice}
        className={`w-full px-4 py-2 text-left hover:bg-emerald-50 text-sm font-medium border-t border-slate-100 flex items-center gap-2 ${
          isRegisteredDevice ? 'text-emerald-600' : 'text-slate-600'
        }`}
      >
        <Monitor className="w-4 h-4" />
        {isRegisteredDevice ? 'Device registered here' : 'Register this device'}
      </button>
    </div>,
    document.body
  ) : null;

  // Integrated header with provider dropdown
  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        data-testid={`provider-selector-${operatory.number}`}
        className="flex items-start gap-1 group text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <h3 className={`${titleSize} font-heading font-bold truncate ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
              {currentProvider?.name || operatory.name}
            </h3>
            {isRegisteredDevice && (
              <Monitor className={`${iconSize} text-emerald-500 flex-shrink-0`} title="This device is registered to this operatory" />
            )}
          </div>
          {currentProvider && size !== 'mini' && (
            <p className={`${subtitleSize} font-normal truncate ${darkMode ? 'text-slate-400' : 'text-slate-400'}`}>{operatory.name}</p>
          )}
        </div>
        <ChevronDown className={`w-3 h-3 mt-1 group-hover:text-slate-600 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''} ${darkMode ? 'text-slate-400' : 'text-slate-400'}`} />
      </button>
      {menu}
    </>
  );
}

// ============ OPERATORY CARD ============
function OperatoryCard({ operatory, doctors, onUpdate, registeredOperatoryId, hygienistNameMap, size = 'normal', buttonColors = {} }) {
  const isRegisteredDevice = registeredOperatoryId === operatory.id;
  const { darkMode } = useTheme();
  
  // Size-based styling
  const getSizeStyles = () => {
    switch (size) {
      case 'compact':
        return {
          card: 'rounded-xl p-3',
          header: 'mb-2',
          buttonSpacing: 'space-y-2',
          titleSize: 'text-base',
          subtitleSize: 'text-xs'
        };
      case 'mini':
        return {
          card: 'rounded-lg p-2',
          header: 'mb-1',
          buttonSpacing: 'space-y-1.5',
          titleSize: 'text-sm',
          subtitleSize: 'text-xs'
        };
      default:
        return {
          card: 'rounded-2xl p-5',
          header: 'mb-4',
          buttonSpacing: 'space-y-3',
          titleSize: 'text-lg',
          subtitleSize: 'text-xs'
        };
    }
  };
  
  const styles = getSizeStyles();
  
  // Get card background based on registered device and dark mode
  const getCardBackground = () => {
    if (isRegisteredDevice) {
      return darkMode 
        ? 'border-emerald-500/50 bg-emerald-900/40' 
        : 'border-emerald-300 bg-emerald-50/60';
    }
    return darkMode 
      ? 'border-slate-600/50 bg-slate-800/60' 
      : 'border-white/20 bg-white/60';
  };
  
  return (
    <div
      data-testid={`operatory-card-${operatory.number}`}
      className={`border backdrop-blur-lg shadow-sm hover:shadow-md transition-all flex flex-col overflow-hidden ${styles.card} ${getCardBackground()}`}
    >
      {/* Header - Operatory name or Provider name with dropdown */}
      <div className={styles.header}>
        <ProviderSelector 
          operatory={operatory} 
          onUpdate={onUpdate} 
          isRegisteredDevice={isRegisteredDevice}
          hygienistNameMap={hygienistNameMap}
          size={size}
        />
      </div>

      {/* 3 Buttons */}
      <div className={styles.buttonSpacing}>
        <PatientFlowButton operatory={operatory} onUpdate={onUpdate} size={size} buttonColors={buttonColors} />
        <DrNeededButton operatory={operatory} doctors={doctors} onUpdate={onUpdate} size={size} buttonColors={buttonColors} />
        <HelpNeededButton operatory={operatory} onUpdate={onUpdate} size={size} buttonColors={buttonColors} />
      </div>
    </div>
  );
}

// ============ OPERATORY GRID ============
export default function OperatoryGrid({ operatories, onUpdate, showAll = false }) {
  const { practice } = usePractice();
  const [doctors, setDoctors] = useState([]);
  const [registeredOperatoryId, setRegisteredOperatoryId] = useState(null);
  const [hygienistNameMap, setHygienistNameMap] = useState({});
  
  // Get button colors from practice settings
  const buttonColors = {
    patient_arrived: practice?.button_color_patient_arrived || '#0284c7',
    patient_seated: practice?.button_color_patient_seated || '#059669',
    dr_in_room: practice?.button_color_dr_in_room || '#4f46e5',
    dr_needed: practice?.button_color_dr_needed || '#f59e0b',
    help_needed: practice?.button_color_help_needed || '#8b5cf6'
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, hygienistNamesRes] = await Promise.all([
          axios.get(`${API}/users`),
          axios.get(`${API}/providers/hygienist-names`)
        ]);
        // Include users with role 'doctor' OR base_role 'doctor' (for doctors who are also admins)
        setDoctors(usersRes.data.filter(u => u.role === 'doctor' || u.base_role === 'doctor'));
        setHygienistNameMap(hygienistNamesRes.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
  }, []);

  // Check if this device is registered to an operatory
  useEffect(() => {
    const checkDeviceRegistration = async () => {
      try {
        const deviceId = getDeviceId();
        const res = await axios.get(`${API}/devices/current?device_id=${deviceId}`);
        if (res.data.registered) {
          setRegisteredOperatoryId(res.data.operatory_id);
        }
      } catch (error) {
        console.error('Failed to check device registration:', error);
      }
    };
    checkDeviceRegistration();
  }, [operatories]); // Re-check when operatories update (in case of new registration)

  // Determine grid config based on count and showAll setting
  // MAX 5 operatories per row
  const getGridConfig = () => {
    const count = operatories.length;
    
    if (!showAll) {
      // Default mode: standard responsive grid, max 5 per row
      if (count <= 3) return { cols: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3', size: 'normal', gap: 'gap-6' };
      if (count <= 5) return { cols: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5', size: 'normal', gap: 'gap-6' };
      // More than 5: wrap to additional rows, max 5 per row
      return { cols: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5', size: 'normal', gap: 'gap-6' };
    }
    
    // Show all mode: shrink cards to fit more, max 5 per row, wrap to rows
    if (count <= 5) return { cols: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5', size: 'normal', gap: 'gap-4' };
    if (count <= 10) return { cols: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5', size: 'compact', gap: 'gap-3' };
    // More than 10: use mini size, max 5 per row
    return { cols: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5', size: 'mini', gap: 'gap-2' };
  };

  const gridConfig = getGridConfig();

  return (
    <div className={`grid ${gridConfig.cols} ${gridConfig.gap}`} data-testid="operatory-grid">
      {operatories.map(operatory => (
        <OperatoryCard
          key={operatory.id}
          operatory={operatory}
          doctors={doctors}
          onUpdate={onUpdate}
          registeredOperatoryId={registeredOperatoryId}
          hygienistNameMap={hygienistNameMap}
          size={gridConfig.size}
          buttonColors={buttonColors}
        />
      ))}
    </div>
  );
}
